import React, { useCallback, useEffect, useState } from "react";
import { hotels } from "../api/endpoints";
import { triggerDownload } from "../utils/download";
import type { HotelProperty, Brand, ManagementCompany, RegionalRepresentative, PropertyContact } from "../types";

export default function PropertiesPage() {
  const PAGE_SIZE = 50;
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [managementCompanies, setManagementCompanies] = useState<ManagementCompany[]>([]);
  const [regionalUsers, setRegionalUsers] = useState<RegionalRepresentative[]>([]);
  const [generalManagers, setGeneralManagers] = useState<PropertyContact[]>([]);
  const [directorsOfSales, setDirectorsOfSales] = useState<PropertyContact[]>([]);
  const [search, setSearch] = useState("");
  const [managementCompanyFilter, setManagementCompanyFilter] = useState(0);
  const [showInactive, setShowInactive] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProp, setEditProp] = useState<HotelProperty | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);
  const [form, setForm] = useState({
    hotel_code: "", name: "", brand_id: 0, management_company_id: 0,
    company_name: "", contact_number: "", website: "", fax: "", address: "", city: "", state: "", zip_code: "",
    guest_rooms: 0, meeting_space_sqft: 0,
    regional_representative_id: 0,
    regional_representative_name: "", regional_representative_email: "",
    general_manager_name: "", property_manager_email: "",
    dos_name: "", dos_email: "",
    doc_name: "", doc_email: "",
    rvp_sales_email: "", rdc_email: "", rdo_email: "",
    additional_email_1: "", additional_email_2: "", additional_email_3: "",
    contract_ref: "", notes: "",
    is_active: true,
  });
  const [codeWarning, setCodeWarning] = useState<string | null>(null);
  const [matchedProperty, setMatchedProperty] = useState<HotelProperty | null>(null);

  const blankForm = {
    hotel_code: "", name: "", brand_id: 0, management_company_id: 0,
    company_name: "", contact_number: "", website: "", fax: "", address: "", city: "", state: "", zip_code: "",
    guest_rooms: 0, meeting_space_sqft: 0,
    regional_representative_id: 0,
    regional_representative_name: "", regional_representative_email: "",
    general_manager_name: "", property_manager_email: "",
    dos_name: "", dos_email: "",
    doc_name: "", doc_email: "",
    rvp_sales_email: "", rdc_email: "", rdo_email: "",
    additional_email_1: "", additional_email_2: "", additional_email_3: "",
    contract_ref: "", notes: "",
    is_active: true,
  };

  const regionalOptions = [...regionalUsers]
    .sort((a, b) => {
      const aName = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email;
      const bName = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email;
      return aName.localeCompare(bName);
    });
  const brandOptions = [...brands].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.id - b.id
  );

  const contactLabel = (contact: PropertyContact) =>
    contact.full_name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email;

  const sortContacts = useCallback(
    (contacts: PropertyContact[]) =>
      [...contacts].sort((left, right) =>
        contactLabel(left).localeCompare(contactLabel(right), undefined, { sensitivity: "base" }) ||
        left.email.localeCompare(right.email, undefined, { sensitivity: "base" })
      ),
    []
  );

  const findSelectedContactId = (name: string, email: string, options: PropertyContact[]) => {
    const normalizedName = name.trim().toLowerCase();
    const normalizedEmail = email.trim().toLowerCase();
    const matched = options.find((option) =>
      (normalizedEmail && option.email.trim().toLowerCase() === normalizedEmail) ||
      (normalizedName && contactLabel(option).trim().toLowerCase() === normalizedName)
    );
    return matched?.id || 0;
  };

  const parseWholeNumber = (value: string) => {
    const digits = value.replace(/\D/g, "");
    return digits ? Number(digits) : 0;
  };

  const loadProperties = useCallback(() => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String((page - 1) * PAGE_SIZE));
    if (showInactive) params.set("active_only", "false");
    if (search) params.set("search", search);
    if (managementCompanyFilter) params.set("mc_id", String(managementCompanyFilter));
    hotels.properties.list(params.toString()).then((res) => {
      setProperties(res.items);
      setTotalCount(res.count);
    });
  }, [search, page, showInactive, managementCompanyFilter]);

  const exportProperties = async () => {
    const params = new URLSearchParams();
    if (showInactive) params.set("active_only", "false");
    if (search) params.set("search", search);
    if (managementCompanyFilter) params.set("mc_id", String(managementCompanyFilter));
    try {
      await triggerDownload(hotels.properties.exportUrl(params.toString()));
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Property export failed." });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  useEffect(() => {
    let cancelled = false;

    const loadSupportingData = async () => {
      const [brandRows, managementCompanyRows, regionalRows, propertyContactRows] = await Promise.all([
        hotels.brands(),
        hotels.managementCompanies.list("limit=500"),
        hotels.regionalRepresentatives(),
        hotels.propertyContacts(),
      ]);
      if (cancelled) {
        return;
      }

      setBrands(brandRows);
      setManagementCompanies(managementCompanyRows.items);
      setRegionalUsers(regionalRows);
      setGeneralManagers(sortContacts(propertyContactRows.general_managers));
      setDirectorsOfSales(sortContacts(propertyContactRows.directors_of_sales));
    };
    loadSupportingData();

    return () => {
      cancelled = true;
    };
  }, [sortContacts]);

  // Live duplicate check on hotel code when adding (not editing)
  useEffect(() => {
    if (editProp || !showForm) {
      setCodeWarning(null);
      setMatchedProperty(null);
      return;
    }
    const code = form.hotel_code.trim();
    if (!code) {
      setCodeWarning(null);
      setMatchedProperty(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await hotels.properties.list(`search=${encodeURIComponent(code)}&limit=5&active_only=false`);
        const exact = res.items.find(
          (p) => p.hotel_code.toLowerCase() === code.toLowerCase()
        );
        if (exact) {
          setCodeWarning(`Hotel code "${exact.hotel_code}" already exists: ${exact.name}`);
          setMatchedProperty(exact);
        } else {
          setCodeWarning(null);
          setMatchedProperty(null);
        }
      } catch {
        setCodeWarning(null);
        setMatchedProperty(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.hotel_code, editProp, showForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    const payload = {
      ...form,
      brand_id: form.brand_id || null,
      management_company_id: form.management_company_id || null,
      regional_representative_id: form.regional_representative_id || null,
    };
    try {
      if (editProp) {
        await hotels.properties.update(editProp.id, payload as any);
      } else {
        await hotels.properties.create(payload as any);
      }
      setShowForm(false);
      setEditProp(null);
      setNotice({ tone: "success", message: editProp ? "Property updated." : "Property added." });
      loadProperties();
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Property save failed." });
    } finally {
      setSaving(false);
    }
  };

  const markInactive = async (propertyId: number) => {
    if (window.confirm("Mark this hotel inactive? It will remain in the system and can be reactivated later.")) {
      try {
        await hotels.properties.delete(propertyId);
        setNotice({ tone: "success", message: "Property marked inactive." });
        loadProperties();
      } catch (error) {
        setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Unable to mark property inactive." });
      }
    }
  };

  const reactivate = async (propertyId: number) => {
    try {
      await hotels.properties.update(propertyId, { is_active: true } as any);
      setNotice({ tone: "success", message: "Property reactivated." });
      loadProperties();
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Unable to reactivate property." });
    }
  };

  const handleEdit = (p: HotelProperty) => {
    setEditProp(p);
    setForm({
      hotel_code: p.hotel_code, name: p.name, brand_id: p.brand?.id || 0,
      management_company_id: p.management_company?.id || 0,
      company_name: p.company_name || "",
      contact_number: p.contact_number, website: p.website || "", fax: p.fax || "", address: p.address,
      city: p.city, state: p.state, zip_code: p.zip_code,
      guest_rooms: p.guest_rooms, meeting_space_sqft: p.meeting_space_sqft,
      regional_representative_id: p.regional_representative_id || 0,
      regional_representative_name: p.regional_representative_name || "",
      regional_representative_email: p.regional_representative_email || "",
      general_manager_name: p.general_manager_name || "",
      property_manager_email: p.property_manager_email || "",
      dos_name: p.dos_name || "",
      dos_email: p.dos_email || "",
      doc_name: p.doc_name || "",
      doc_email: p.doc_email || "",
      rvp_sales_email: p.rvp_sales_email || "",
      rdc_email: p.rdc_email || "",
      rdo_email: p.rdo_email || "",
      additional_email_1: p.additional_email_1 || "",
      additional_email_2: p.additional_email_2 || "",
      additional_email_3: p.additional_email_3 || "",
      contract_ref: p.contract_ref || "",
      notes: p.notes || "",
      is_active: p.is_active,
    });
    setShowForm(true);
  };

  const handleRegionalRepresentativeChange = (value: number) => {
    const selectedUser = regionalUsers.find((user) => user.id === value);
    setForm({
      ...form,
      regional_representative_id: value,
      regional_representative_name:
        value === 0 ? form.regional_representative_name : `${selectedUser?.first_name || ""} ${selectedUser?.last_name || ""}`.trim(),
      regional_representative_email:
        value === 0 ? form.regional_representative_email : selectedUser?.email || "",
    });
  };

  const handleGeneralManagerChange = (value: number) => {
    const selectedUser = generalManagers.find((user) => user.id === value);
    setForm({
      ...form,
      general_manager_name: value === 0 ? form.general_manager_name : (selectedUser ? contactLabel(selectedUser) : ""),
      property_manager_email: value === 0 ? form.property_manager_email : selectedUser?.email || "",
    });
  };

  const handleDirectorOfSalesChange = (value: number) => {
    const selectedUser = directorsOfSales.find((user) => user.id === value);
    setForm({
      ...form,
      dos_name: value === 0 ? form.dos_name : (selectedUser ? contactLabel(selectedUser) : ""),
      dos_email: value === 0 ? form.dos_email : selectedUser?.email || "",
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Hotel Properties</h1>
        <button onClick={() => { setEditProp(null); setForm(blankForm); setCodeWarning(null); setMatchedProperty(null); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Add Property
        </button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)_auto_auto] md:items-center">
        <input
          placeholder="Search by code, name, or city..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="border rounded px-3 py-2 w-full"
        />
        <select
          value={managementCompanyFilter}
          onChange={(e) => { setManagementCompanyFilter(Number(e.target.value)); setPage(1); }}
          className="border rounded px-3 py-2 w-full"
        >
          <option value={0}>All management companies</option>
          {managementCompanies.map((company) => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>
        <label className="flex shrink-0 items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
          />
          <span>Show inactive hotels</span>
        </label>
        <button type="button" onClick={exportProperties} className="button-secondary shrink-0 px-4 py-2 text-sm">
          Export CSV
        </button>
      </div>
      {notice ? (
        <div
          className={`mb-4 rounded border px-4 py-3 text-sm ${
            notice.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {notice.message}
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Brand</th>
              <th className="px-4 py-3 text-left">HMC</th>
              <th className="px-4 py-3 text-left">City/State</th>
              <th className="px-4 py-3 text-left">Rooms</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((p) => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs">{p.hotel_code}</td>
                <td className="px-4 py-3">{p.name}</td>
                <td className="px-4 py-3">{p.brand?.name || "-"}</td>
                <td className="px-4 py-3 text-xs">{p.management_company?.name || "-"}</td>
                <td className="px-4 py-3">{p.city}{p.state ? `, ${p.state}` : ""}</td>
                <td className="px-4 py-3">{p.guest_rooms}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                    {p.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                  {p.is_active ? (
                    <button onClick={() => markInactive(p.id)} className="text-red-600 hover:underline text-xs">Mark Inactive</button>
                  ) : (
                    <button onClick={() => reactivate(p.id)} className="text-emerald-700 hover:underline text-xs">Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <p className="text-sm text-slate-600">
              Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage(1)} disabled={page === 1} className="button-ghost text-xs px-2 py-1 disabled:opacity-40">First</button>
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1} className="button-ghost text-xs px-2 py-1 disabled:opacity-40">Prev</button>
              <span className="text-sm font-medium text-slate-700">Page {page} of {totalPages}</span>
              <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page === totalPages} className="button-ghost text-xs px-2 py-1 disabled:opacity-40">Next</button>
              <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="button-ghost text-xs px-2 py-1 disabled:opacity-40">Last</button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
            <h2 className="text-lg font-bold mb-4">{editProp ? "Edit Property" : "Add Property"}</h2>
            <p className="mb-4 text-sm text-slate-500">
              Use this setup form to maintain hotel contacts, website, management company routing, and review recipients.
            </p>
            {notice?.tone === "danger" ? (
              <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {notice.message}
              </div>
            ) : null}
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Hotel Code" value={form.hotel_code} onChange={(e) => setForm({ ...form, hotel_code: e.target.value })} className={`border rounded px-3 py-2 ${codeWarning && !editProp ? "border-amber-400" : ""}`} required disabled={!!editProp} />
                <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border rounded px-3 py-2" required />
                {codeWarning && !editProp && (
                  <div className="col-span-2 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-2">
                    <span>⚠️ {codeWarning}</span>
                    {matchedProperty && (
                      <button
                        type="button"
                        className="shrink-0 rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
                        onClick={() => {
                          setShowForm(false);
                          setCodeWarning(null);
                          setMatchedProperty(null);
                          handleEdit(matchedProperty);
                        }}
                      >
                        Edit existing
                      </button>
                    )}
                  </div>
                )}
                <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: Number(e.target.value) })} className="border rounded px-3 py-2">
                  <option value={0}>Select Brand</option>
                  {brandOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select value={form.management_company_id} onChange={(e) => setForm({ ...form, management_company_id: Number(e.target.value) })} className="border rounded px-3 py-2">
                  <option value={0}>Select HMC</option>
                  {managementCompanies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                </select>
                <input placeholder="Hotel Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Contact Number" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Fax" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Mgmt. Company Label (optional)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border rounded px-3 py-2 col-span-2" />
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Guest Rooms" inputMode="numeric" pattern="[0-9]*" value={form.guest_rooms} onChange={(e) => setForm({ ...form, guest_rooms: parseWholeNumber(e.target.value) })} className="border rounded px-3 py-2" />
                <input placeholder="Meeting Space (sq ft)" inputMode="numeric" pattern="[0-9]*" value={form.meeting_space_sqft} onChange={(e) => setForm({ ...form, meeting_space_sqft: parseWholeNumber(e.target.value) })} className="border rounded px-3 py-2" />
                <select value={form.regional_representative_id} onChange={(e) => handleRegionalRepresentativeChange(Number(e.target.value))} className="border rounded px-3 py-2">
                  <option value={0}>Select Regional Rep</option>
                  {regionalOptions.map((user) => <option key={user.id} value={user.id}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</option>)}
                </select>
                <input placeholder="Regional Rep Name" value={form.regional_representative_name} onChange={(e) => setForm({ ...form, regional_representative_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Regional Rep Email" value={form.regional_representative_email} onChange={(e) => setForm({ ...form, regional_representative_email: e.target.value })} className="border rounded px-3 py-2 col-span-2" />
                <select
                  value={findSelectedContactId(form.general_manager_name, form.property_manager_email, generalManagers)}
                  onChange={(e) => handleGeneralManagerChange(Number(e.target.value))}
                  className="border rounded px-3 py-2"
                >
                  <option value={0}>Select GM or type below</option>
                  {generalManagers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {`${contactLabel(user)}${user.email ? ` - ${user.email}` : ""}`}
                    </option>
                  ))}
                </select>
                <input placeholder="GM Name" value={form.general_manager_name} onChange={(e) => setForm({ ...form, general_manager_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="GM Email" value={form.property_manager_email} onChange={(e) => setForm({ ...form, property_manager_email: e.target.value })} className="border rounded px-3 py-2" />
                <select
                  value={findSelectedContactId(form.dos_name, form.dos_email, directorsOfSales)}
                  onChange={(e) => handleDirectorOfSalesChange(Number(e.target.value))}
                  className="border rounded px-3 py-2"
                >
                  <option value={0}>Select DOS or type below</option>
                  {directorsOfSales.map((user) => (
                    <option key={user.id} value={user.id}>
                      {`${contactLabel(user)}${user.email ? ` - ${user.email}` : ""}`}
                    </option>
                  ))}
                </select>
                <input placeholder="DOS Name" value={form.dos_name} onChange={(e) => setForm({ ...form, dos_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOS Email" value={form.dos_email} onChange={(e) => setForm({ ...form, dos_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOC Name" value={form.doc_name} onChange={(e) => setForm({ ...form, doc_name: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="DOC Email" value={form.doc_email} onChange={(e) => setForm({ ...form, doc_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RVP Sales Email" value={form.rvp_sales_email} onChange={(e) => setForm({ ...form, rvp_sales_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RDC Email" value={form.rdc_email} onChange={(e) => setForm({ ...form, rdc_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="RDO Email" value={form.rdo_email} onChange={(e) => setForm({ ...form, rdo_email: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 1" value={form.additional_email_1} onChange={(e) => setForm({ ...form, additional_email_1: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 2" value={form.additional_email_2} onChange={(e) => setForm({ ...form, additional_email_2: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Other Email 3" value={form.additional_email_3} onChange={(e) => setForm({ ...form, additional_email_3: e.target.value })} className="border rounded px-3 py-2" />
                <input placeholder="Contract Ref / Internal Tag" value={form.contract_ref} onChange={(e) => setForm({ ...form, contract_ref: e.target.value })} className="border rounded px-3 py-2" />
                <label className="col-span-2 flex items-center gap-2 rounded border px-3 py-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  <span>Property is active</span>
                </label>
                <textarea placeholder="Competition Comments / Property Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border rounded px-3 py-2 col-span-2 min-h-[120px]" />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
